import { useCallback, useEffect, useState } from 'react';

/**
 * Theme, in three states: light, dark, or unset — where "unset" means follow
 * the operating system.
 *
 * The stamp goes on the root element and the choice is remembered per browser.
 * A blocked or full localStorage throws on read and on write, so both are
 * guarded: a viewer in a private window gets the OS theme rather than a blank
 * screen.
 */

const STORAGE_KEY = 'fleet.theme';

function readStored() {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'dark' || value === 'light' ? value : null;
  } catch {
    return null;
  }
}

function systemTheme() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useTheme() {
  const [stored, setStored] = useState(readStored);
  const [system, setSystem] = useState(systemTheme);

  // Follow the OS while the viewer has not chosen for themselves.
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return undefined;
    const onChange = (event) => setSystem(event.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const theme = stored ?? system;

  useEffect(() => {
    if (stored) document.documentElement.setAttribute('data-theme', stored);
    else document.documentElement.removeAttribute('data-theme');
  }, [stored]);

  const setTheme = useCallback((next) => {
    setStored(next);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage unavailable — the theme still applies for this session.
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return { theme, setTheme, toggleTheme, isSystem: stored === null };
}
